import type { ReactNode } from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Loader,
} from "@coval/ui"
import { Trash2 } from "lucide-react"

import {
  useTemplateDependenciesAvailability,
  type TemplateDependencyResourceKind,
} from "../transport/hooks/useTemplateDependenciesAvailability"

/** One selected row, named so the dialog can say what it is about to delete. */
interface BulkDeleteResource {
  id: string
  name: string
}

interface TemplateDependencyBulkDeleteDialogProps {
  confirmTestId?: string
  isDeleting?: boolean
  /** Shown under the prompt, e.g. that the delete can be undone. */
  note?: ReactNode
  /** Receives only the resources no active Template references. */
  onConfirm: (deletableIds: string[]) => void
  onOpenChange: (open: boolean) => void
  open: boolean
  resourceKind: TemplateDependencyResourceKind
  resources: BulkDeleteResource[]
}

const RESOURCE_LABELS_BY_KIND: Record<
  TemplateDependencyResourceKind,
  { singular: string; plural: string }
> = {
  agent: { singular: "agent", plural: "agents" },
  metric: { singular: "metric", plural: "metrics" },
  mutation: { singular: "mutation", plural: "mutations" },
  persona: { singular: "persona", plural: "personas" },
  testSet: { singular: "test set", plural: "test sets" },
}

/**
 * The delete confirmation for a multi-row selection, holding the same line
 * `TemplateDependencyDeleteDialog` holds for one row: a resource an active
 * Template still references cannot be deleted. The rest of the selection can,
 * so a partly blocked selection offers to delete what is left rather than
 * refusing the whole gesture, and names what it is leaving behind.
 */
export function TemplateDependencyBulkDeleteDialog({
  open,
  ...props
}: TemplateDependencyBulkDeleteDialogProps) {
  if (!open) {
    return null
  }

  return <OpenTemplateDependencyBulkDeleteDialog {...props} open />
}

function OpenTemplateDependencyBulkDeleteDialog({
  confirmTestId,
  isDeleting = false,
  note,
  onConfirm,
  onOpenChange,
  open,
  resourceKind,
  resources,
}: TemplateDependencyBulkDeleteDialogProps) {
  const resourceIds = resources.map((resource) => resource.id)
  const availability = useTemplateDependenciesAvailability(
    resourceIds,
    resourceKind,
  )
  const labels = RESOURCE_LABELS_BY_KIND[resourceKind]
  const nameById = new Map(
    resources.map((resource) => [resource.id, resource.name]),
  )

  const isReady = availability.status === "ready"
  const blocked = isReady ? availability.blocked : []
  const deletableIds = isReady ? availability.deletableIds : []
  const canDelete = deletableIds.length > 0
  const isCloseOnly = isReady ? !canDelete : availability.status === "error"

  const resourceWord = (count: number) =>
    count === 1 ? labels.singular : labels.plural

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isDeleting) {
          onOpenChange(next)
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {`Delete ${resources.length} ${resourceWord(resources.length)}`}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {(availability.status === "idle" ||
            availability.status === "checking") && (
            <div className="flex min-h-20 items-center justify-center">
              <Loader size="md" aria-label="Checking Templates" />
            </div>
          )}
          {availability.status === "error" && (
            <p className="text-coval-text-muted">
              We couldn&apos;t check whether Templates use{" "}
              {resources.length === 1
                ? `this ${labels.singular}`
                : `these ${labels.plural}`}
              . Close this dialog and try again.
            </p>
          )}
          {isReady && (
            <div className="space-y-4">
              {canDelete && (
                <p className="font-medium text-coval-text">
                  {`Are you sure you want to delete ${deletableIds.length} ${resourceWord(
                    deletableIds.length,
                  )}?`}
                </p>
              )}
              {blocked.length > 0 && (
                <section aria-labelledby="blocked-resources-heading">
                  <h3
                    id="blocked-resources-heading"
                    className="mb-2 text-sm font-semibold text-coval-text"
                  >
                    {blocked.length === 1
                      ? `1 ${labels.singular} can’t be deleted because Templates rely on it`
                      : `${blocked.length} ${labels.plural} can’t be deleted because Templates rely on them`}
                  </h3>
                  <ul className="max-h-60 divide-y divide-coval-border overflow-y-auto rounded-md border border-coval-border">
                    {blocked.map((blockedResource) => (
                      <li
                        key={blockedResource.id}
                        className="flex items-center gap-3 px-3 py-2.5"
                      >
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-coval-text">
                          {nameById.get(blockedResource.id) ||
                            blockedResource.id}
                        </span>
                        <span className="shrink-0 text-xs text-coval-text-muted">
                          {blockedResource.templates.length === 1
                            ? "1 Template"
                            : `${blockedResource.templates.length} Templates`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {canDelete && note ? (
                <p className="text-xs text-coval-text-muted">{note}</p>
              ) : null}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
            aria-label={isCloseOnly ? "Close" : "Cancel delete"}
          >
            {isCloseOnly ? "Close" : "Cancel"}
          </Button>
          {canDelete && (
            <Button
              variant="default"
              onClick={() => onConfirm(deletableIds)}
              loading={isDeleting}
              data-testid={confirmTestId}
              aria-label="Confirm delete"
            >
              {!isDeleting && <Trash2 className="h-4 w-4" />}
              {`Delete ${deletableIds.length}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
